<!-- app-subdomain-creators/src/accountPages/CreatorPageEditor.vue -->
<template>
  <div class="min-h-full bg-transparent px-5 py-6 sm:px-6 lg:px-8 lg:py-8">
    <div class="mx-auto w-full max-w-6xl">
      <!-- Header -->
      <div class="overflow-hidden rounded-2xl border border-blue-100 bg-gradient-to-br from-white via-white to-blue-50 px-5 py-5 shadow-sm sm:px-6">
        <div class="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div class="flex items-start gap-4">
            <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#2D8CFF] text-white shadow-lg shadow-blue-200/70">
              <RectangleStackIcon class="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#2D8CFF]">Website builder</p>
              <h2 class="mt-1 text-2xl font-semibold text-zinc-900">Site Page Editor</h2>
              <p class="mt-1 max-w-2xl text-sm leading-6 text-zinc-600">Build pages from scratch, start from a template, or reuse your own saved designs.</p>
            </div>
          </div>
          <button
            type="button"
            class="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#2D8CFF] px-4 py-2.5 text-sm font-extrabold text-white shadow-lg shadow-blue-200/70 transition hover:bg-[#1f77e6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D8CFF] focus-visible:ring-offset-2"
            @click="openCreateModal()"
          >
            <PlusIcon class="h-4 w-4" aria-hidden="true" />
            Create new page
          </button>
        </div>
      </div>

      <nav class="mt-3 flex gap-8 px-1" role="tablist" aria-label="Page editor views">
        <button type="button" role="tab" :aria-selected="activeTab === 'pages'" class="whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors" :class="activeTab === 'pages' ? 'border-[#2D8CFF] text-[#2D8CFF]' : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-700'" @click="activeTab = 'pages'; searchQuery = ''">Pages</button>
        <button type="button" role="tab" :aria-selected="activeTab === 'templates'" class="whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors" :class="activeTab === 'templates' ? 'border-[#2D8CFF] text-[#2D8CFF]' : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-700'" @click="activeTab = 'templates'; searchQuery = ''">Templates</button>
      </nav>
    </div>

    <transition name="page-editor-tab" mode="out-in">
      <div
        :key="activeTab"
        class="mx-auto mt-5 w-full"
        :class="activeTab === 'templates' ? 'max-w-[1600px]' : 'max-w-6xl'"
      >
        <div class="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
        <!-- Search Bar -->
        <div class="flex w-full items-center border-b border-zinc-200 px-5 py-4 sm:px-6">
          <div class="relative w-full max-w-xl">
          <MagnifyingGlassIcon
            class="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-400"
          />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="activeTab === 'pages' ? 'Search Pages' : 'Search Templates'"
            class="w-full rounded-xl border border-zinc-300 py-2.5 pl-10 pr-4 text-sm text-zinc-700 placeholder-zinc-400 transition focus:border-[#2D8CFF] focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
        </div>
      </div>

      <!-- Create Page Modal -->
      <TransitionRoot
        as="template"
        :show="showModal"
        @after-leave="onModalAfterLeave"
      >
        <Dialog
          class="relative z-50"
          @close="showModal = false"
        >
          <!-- Backdrop -->
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="ease-in duration-200"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <div class="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity" />
          </TransitionChild>

          <!-- Modal container -->
          <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enter-to="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leave-from="opacity-100 translate-y-0 sm:scale-100"
                leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel
                  class="relative transform overflow-hidden rounded-xl bg-white px-6 pb-6 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md"
                >
                  <h3 class="text-lg font-semibold text-zinc-800 mb-4">
                    Create New Page
                  </h3>

                  <div v-if="selectedCreationTemplate" class="mb-4 rounded-xl border border-blue-100 bg-blue-50 px-3.5 py-3">
                    <p class="text-[10px] font-extrabold uppercase tracking-[.14em] text-blue-600">Starting template</p>
                    <p class="mt-1 text-sm font-bold text-blue-950">{{ selectedCreationTemplate.name }}</p>
                    <p class="mt-1 text-xs leading-5 text-blue-700">The structured sections, rows, columns, media, and elements will be copied into the new page.</p>
                  </div>

                  <div class="space-y-3">
                    <div>
                      <label class="block text-sm font-medium mb-1 text-zinc-700">Title</label>
                      <input
                        v-model="newTitle"
                        type="text"
                        class="w-full border border-zinc-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#2D8CFF]"
                        placeholder="e.g. About Us"
                        @input="onNewTitleInput"
                      >
                    </div>
                    <div>
                      <label class="block text-sm font-medium mb-1 text-zinc-700">Slug (URL path)</label>
                      <input
                        v-model="newSlug"
                        type="text"
                        class="w-full border border-zinc-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#2D8CFF]"
                        placeholder="e.g. about-us"
                        @input="onNewSlugInput"
                      >
                      <p class="mt-1.5 text-xs text-zinc-500">A unique suggestion is generated automatically. You can change it before creating the page.</p>
                    </div>

                    <p
                      v-if="error"
                      class="text-red-600 text-sm"
                    >
                      {{ error }}
                    </p>

                    <div class="flex justify-end gap-3 mt-4">
                      <button
                        class="text-zinc-500 hover:text-zinc-700 text-sm font-medium transition"
                        :disabled="loading"
                        @click="showModal = false"
                      >
                        Cancel
                      </button>
                      <button
                        class="bg-[#2D8CFF] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#1b6ad1] transition disabled:opacity-60 disabled:cursor-not-allowed"
                        :disabled="loading"
                        @click="createPage"
                      >
                        {{ loading ? 'Creating...' : 'Create' }}
                      </button>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </TransitionRoot>

      <!-- Page List + Pagination Wrapper -->
      <div v-if="activeTab === 'pages'" class="flex min-h-[62vh] flex-col p-5 sm:p-6">
        <!-- Page List -->
        <div class="space-y-3 flex-1">
          <div
            v-if="loadError"
            class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {{ loadError }}
          </div>

          <div
            v-for="page in filteredPages"
            :key="page.id"
            class="flex justify-between items-center bg-white border border-zinc-200 rounded-xl px-5 py-4 hover:border-blue-200 hover:shadow-md transition-all duration-200"
          >
            <!-- Page content -->
            <div>
              <p class="font-medium text-zinc-800">
                {{ getDisplayPageTitle(page) }}
              </p>
              <div class="flex items-center gap-2 mt-1">
                <a
                  v-if="liveDomain"
                  :href="`${liveDomain}/${page.slug}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-blue-600 hover:underline focus:outline-none focus:ring-0"
                >
                  {{ `${liveDomain}/${page.slug}` }}
                </a>
                <p
                  v-else
                  class="text-sm text-zinc-400"
                >
                  Loading domain...
                </p>
              </div>

            </div>

            <!-- Right-side controls -->
            <div class="flex gap-3 items-center">
              <!-- Copied ✓ + Copy icon -->
              <div class="flex items-center gap-2">
                <span
                  v-if="copiedLink === `${liveDomain}/${page.slug}`"
                  class="text-sm text-green-600 font-medium"
                >
                  Copied ✓
                </span>
                <div class="relative group">
                  <ClipboardIcon
                    v-if="liveDomain"
                    class="h-5 w-5 text-zinc-400 hover:text-[#ff9203] cursor-pointer transition"
                    @click="copyLink(`${liveDomain}/${page.slug}`)"
                  />
                  <span
                    class="pointer-events-none absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black/70 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  >
                    Copy Link
                  </span>
                </div>
              </div>

              <!-- Save as reusable template -->
              <div class="relative group">
                <BookmarkSquareIcon
                  class="w-5 h-5 text-zinc-400 hover:text-[#c342ff] cursor-pointer transition"
                  @click="openSavePageTemplate(page)"
                />
                <span class="pointer-events-none absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black/70 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200">Save as template</span>
              </div>

              <!-- Edit -->
              <div class="relative group">
                <PencilIcon
                  class="w-5 h-5 text-zinc-400 hover:text-[#2D8CFF] cursor-pointer transition"
                  @click="openSiteEditor(page)"
                />
                <span
                  class="pointer-events-none absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black/70 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                >
                  Edit
                </span>
              </div>

              <!-- Delete -->
              <div
                v-if="!isProtectedOnboardingPage(page)"
                class="relative group"
              >
                <TrashIcon
                  class="w-5 h-5 text-zinc-400 hover:text-[#ff1b13] cursor-pointer transition"
                  @click="openDeleteModal(page)"
                />
                <span
                  class="pointer-events-none absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black/70 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                >
                  Delete
                </span>
              </div>
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-if="totalItems === 0"
            class="text-center py-10 text-zinc-500 border border-dashed border-zinc-300 rounded-xl bg-white"
          >
            {{ searchQuery ? 'We can’t find any pages from this search.' : 'No pages created yet.' }}
          </div>
        </div>

        <!-- Pagination Footer -->
        <div
          v-if="totalItems > 0"
          class="mt-auto flex items-center justify-between border-t border-zinc-200 pt-6 text-sm text-gray-700"
        >
          <!-- Left -->
          <p>
            Showing
            <span class="font-semibold">{{ startIndex }}</span>
            to
            <span class="font-semibold">{{ endIndex }}</span>
            of
            <span class="font-semibold">{{ totalItems }}</span>
            results
          </p>

          <!-- Right -->
          <div class="flex items-center gap-4">
            <label class="flex items-center gap-2 text-sm text-gray-700">
              <span>Rows per page:</span>
              <PlatformaSelect
                v-model.number="itemsPerPage"
                class="min-w-[110px]"
                :options="rowsPerPageOptions"
                menu-placement="top"
              />
            </label>
            <p>
              Page
              <span class="font-semibold">{{ currentPage }}</span>
              of
              <span class="font-semibold">{{ totalPages }}</span>
            </p>
            <div class="flex gap-2">
              <button
                :disabled="currentPage === 1"
                class="relative inline-flex items-center px-[10px] py-[6px] border border-gray-300 text-xs font-medium rounded-lg text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:ring-blue focus:border-[#2D8CFF] active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="changePage(currentPage - 1)"
              >
                Previous
              </button>
              <button
                :disabled="currentPage === totalPages"
                class="relative inline-flex items-center px-[10px] py-[6px] border border-gray-300 text-xs font-medium rounded-lg text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:ring-blue focus:border-[#2D8CFF] active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="changePage(currentPage + 1)"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

          <div v-else class="min-h-[62vh] bg-zinc-50/60 p-5 sm:p-6">
            <nav v-if="!templatesLoading" class="mb-5 flex gap-8" role="tablist" aria-label="Template library views">
              <button type="button" role="tab" :aria-selected="templateLibraryTab === 'all'" class="whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors" :class="templateLibraryTab === 'all' ? 'border-[#2D8CFF] text-[#2D8CFF]' : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-700'" @click="templateLibraryTab = 'all'">All Templates</button>
              <button type="button" role="tab" :aria-selected="templateLibraryTab === 'saved'" class="inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors" :class="templateLibraryTab === 'saved' ? 'border-[#2D8CFF] text-[#2D8CFF]' : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-700'" @click="templateLibraryTab = 'saved'"><span>Saved Templates</span><span class="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">{{ customPageTemplates.length }}</span></button>
            </nav>
            <div v-if="templatesLoading" class="rounded-2xl border border-zinc-200 bg-white px-6 py-16 text-center text-sm font-medium text-zinc-500">Loading templates…</div>
            <transition v-else name="template-library-tab" mode="out-in">
              <div :key="templateLibraryTab">
                <div v-if="filteredTemplates.length" class="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                  <article v-for="template in filteredTemplates" :key="template.id" class="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
                    <button type="button" class="group relative block h-[230px] w-full overflow-hidden bg-zinc-100 text-left" @click="previewTemplate = template">
                      <div v-if="template.id === 'blank'" class="absolute inset-0 grid place-items-center bg-gradient-to-br from-zinc-50 to-zinc-200">
                        <div class="rounded-xl border border-dashed border-zinc-300 bg-white/90 px-8 py-6 text-center shadow-sm"><PlusIcon class="mx-auto h-7 w-7 text-pink-500" /><p class="mt-2 text-sm font-bold text-zinc-700">Blank Canvas</p></div>
                      </div>
                      <div v-else class="template-card-canvas pointer-events-none select-none"><PageRenderer :doc="template.doc" :selected-index="-1" viewport-mode="desktop" /></div>
                      <div class="absolute inset-0 grid place-items-center bg-zinc-950/0 transition group-hover:bg-zinc-950/20"><span class="translate-y-2 rounded-xl bg-white px-4 py-2 text-sm font-extrabold text-zinc-900 opacity-0 shadow-lg transition group-hover:translate-y-0 group-hover:opacity-100">Preview</span></div>
                    </button>
                    <div class="p-5">
                      <div class="flex items-start justify-between gap-3"><div><span class="inline-flex rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide" :class="template.source === 'creator' ? 'bg-violet-50 text-violet-600' : 'bg-pink-50 text-pink-600'">{{ template.category }}</span><h3 class="mt-3 text-lg font-extrabold text-zinc-950">{{ template.name }}</h3></div><button v-if="template.source === 'creator'" type="button" class="rounded-lg p-2 text-zinc-400 hover:bg-red-50 hover:text-red-600" aria-label="Delete saved template" @click="deleteCustomTemplate(template)"><TrashIcon class="h-4 w-4" /></button></div>
                      <p class="mt-1 min-h-[40px] text-sm leading-5 text-zinc-500">{{ template.description }}</p>
                      <div class="mt-3 flex flex-wrap gap-2 text-[10px] font-bold text-zinc-600"><span class="rounded-full bg-zinc-100 px-2 py-1">{{ template.stats?.sections || 0 }} sections</span><span class="rounded-full bg-zinc-100 px-2 py-1">{{ template.stats?.elements || 0 }} elements</span><span class="rounded-full bg-zinc-100 px-2 py-1">{{ template.stats?.images || 0 }} images</span></div>
                      <div class="mt-5 grid grid-cols-2 gap-3"><button type="button" class="rounded-xl border border-zinc-200 px-4 py-2.5 text-sm font-bold text-zinc-700 hover:bg-zinc-50" @click="previewTemplate = template"><EyeIcon class="mr-1 inline h-4 w-4" />Preview</button><button type="button" class="rounded-xl bg-[#2D8CFF] px-4 py-2.5 text-sm font-extrabold text-white hover:bg-[#1b6ad1]" @click="openCreateModal(template)">Use template</button></div>
                    </div>
                  </article>
                </div>
                <div v-else class="rounded-2xl border border-dashed border-zinc-300 bg-white px-6 py-16 text-center text-sm text-zinc-500">{{ templateEmptyStateMessage }}</div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </transition>
  </div>

  <TransitionRoot as="template" :show="Boolean(previewTemplate)">
    <Dialog class="relative z-[70]" @close="previewTemplate = null">
      <TransitionChild as="template" enter="ease-out duration-200" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-150" leave-from="opacity-100" leave-to="opacity-0"><div class="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm" /></TransitionChild>
      <div class="page-template-preview-scroll fixed inset-0 overflow-y-auto p-4 sm:p-7"><div class="flex min-h-full items-center justify-center"><TransitionChild as="template" enter="ease-out duration-200" enter-from="opacity-0 translate-y-3 scale-[.98]" enter-to="opacity-100 translate-y-0 scale-100" leave="ease-in duration-150" leave-from="opacity-100 translate-y-0 scale-100" leave-to="opacity-0 translate-y-3 scale-[.98]"><DialogPanel class="flex max-h-[94vh] w-full max-w-[1400px] flex-col overflow-hidden rounded-2xl bg-zinc-100 shadow-2xl"><header class="flex items-center justify-between border-b border-zinc-200 bg-white px-5 py-4"><div><DialogTitle class="text-lg font-extrabold text-zinc-950">{{ previewTemplate?.name }}</DialogTitle><p class="text-xs text-zinc-500">Exact structured page preview</p></div><button type="button" class="rounded-lg bg-zinc-100 px-3 py-2 text-sm font-bold text-zinc-600 hover:bg-zinc-200" @click="previewTemplate = null">Close</button></header><div class="page-template-preview-canvas-scroll flex-1 overflow-auto p-5"><div class="mx-auto max-w-[1240px] overflow-hidden bg-white shadow-xl"><PageRenderer v-if="previewTemplate" class="select-none" :doc="previewTemplate.doc" :selected-index="-1" viewport-mode="desktop" /></div></div><footer class="flex items-center justify-between gap-4 border-t border-zinc-200 bg-white px-5 py-4"><p class="text-xs text-zinc-500">Selecting this template will open the Create Page form with a unique suggested slug.</p><button type="button" class="rounded-xl bg-[#2D8CFF] px-5 py-2.5 text-sm font-extrabold text-white hover:bg-[#1b6ad1]" @click="openCreateModal(previewTemplate); previewTemplate = null">Use this template</button></footer></DialogPanel></TransitionChild></div></div>
    </Dialog>
  </TransitionRoot>

  <TransitionRoot as="template" :show="saveTemplateOpen">
    <Dialog class="relative z-[75]" @close="saveTemplateOpen = false">
      <TransitionChild as="template" enter="ease-out duration-200" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-150" leave-from="opacity-100" leave-to="opacity-0"><div class="fixed inset-0 bg-zinc-950/70 backdrop-blur-sm" /></TransitionChild>
      <div class="fixed inset-0 overflow-y-auto p-4"><div class="flex min-h-full items-center justify-center"><TransitionChild as="template" enter="ease-out duration-200" enter-from="opacity-0 translate-y-3 scale-[.98]" enter-to="opacity-100 translate-y-0 scale-100" leave="ease-in duration-150" leave-from="opacity-100 translate-y-0 scale-100" leave-to="opacity-0 translate-y-3 scale-[.98]"><DialogPanel class="w-full max-w-md rounded-2xl bg-white p-6 text-left shadow-2xl"><DialogTitle class="text-lg font-extrabold text-zinc-950">Save page as template</DialogTitle><p class="mt-1 text-sm leading-6 text-zinc-500">The page’s complete draft—including responsive settings and media references—will be reusable on future pages.</p><div class="mt-5 space-y-4"><label class="block"><span class="mb-1 block text-sm font-bold text-zinc-700">Template name</span><input v-model="savedTemplateName" class="w-full rounded-xl border border-zinc-300 px-3 py-2.5 text-sm focus:border-[#2D8CFF] focus:outline-none focus:ring-1 focus:ring-[#2D8CFF]" maxlength="180"></label><label class="block"><span class="mb-1 block text-sm font-bold text-zinc-700">Description <span class="font-normal text-zinc-400">(optional)</span></span><textarea v-model="savedTemplateDescription" class="h-24 w-full resize-none rounded-xl border border-zinc-300 px-3 py-2.5 text-sm focus:border-[#2D8CFF] focus:outline-none focus:ring-1 focus:ring-[#2D8CFF]" maxlength="500" /></label><p v-if="saveTemplateError" class="text-sm font-medium text-red-600">{{ saveTemplateError }}</p></div><div class="mt-6 flex justify-end gap-3"><button type="button" class="rounded-xl px-4 py-2.5 text-sm font-bold text-zinc-500 hover:bg-zinc-100" :disabled="savingTemplate" @click="saveTemplateOpen = false">Cancel</button><button type="button" class="rounded-xl bg-[#2D8CFF] px-4 py-2.5 text-sm font-extrabold text-white disabled:opacity-60" :disabled="savingTemplate" @click="savePageAsTemplate">{{ savingTemplate ? 'Saving…' : 'Save template' }}</button></div></DialogPanel></TransitionChild></div></div>
    </Dialog>
  </TransitionRoot>





  <!-- ✅ Double Confirmation Modal for Page Delete -->
  <TransitionRoot
    as="template"
    :show="confirmOpen"
  >
    <Dialog
      class="relative z-50"
      @close="confirmOpen = false"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm transition-opacity" />
      </TransitionChild>

      <div class="fixed top-[20%] inset-x-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-5 pb-5 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
            >
              <div class="sm:flex sm:items-start">
                <div
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
                >
                  <ExclamationTriangleIcon
                    class="h-6 w-6 text-red-600"
                    aria-hidden="true"
                  />
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle
                    as="h3"
                    class="text-base font-semibold leading-6 text-gray-900"
                  >
                    Delete Page
                  </DialogTitle>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">
                      Are you sure you want to delete your
                      <br>
                      <strong>{{ getDisplayPageTitle(selectedPage) }}</strong> Page?
                      <br>
                      The page and link at:
                      <br>
                      <a
                        v-if="selectedPage && liveDomain"
                        :href="`${liveDomain}/${selectedPage.slug}`"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-blue-600 hover:underline focus:outline-none focus:ring-0"
                      >
                        {{ `${liveDomain}/${selectedPage.slug}` }}
                      </a>
                      <br>
                      will no longer work.
                      <br>
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  @click="confirmDelete"
                >
                  <svg
                    v-if="isProcessing && activeAction === 'delete'"
                    class="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                    />
                  </svg>
                  <span v-else>Delete Page</span>
                </button>
                <button
                  type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  @click="confirmOpen = false"
                >
                  Cancel
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>


<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { emitCreatorPlanLimitPrompt } from '@/utils/creatorPlanLimitPrompt'
import PlatformaSelect from '@/Components/PlatformaSelect.vue'
import PageRenderer from '@/Components/PageRenderer.vue'
import { buildPageTemplate, pageTemplates } from '@/utils/pageTemplates'

import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { BookmarkSquareIcon, ClipboardIcon, EyeIcon, PencilIcon, PlusIcon, RectangleStackIcon, TrashIcon, ExclamationTriangleIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline'

const router = useRouter()
const pendingRedirect = ref(null)
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL

const pages = ref([])
const allPages = ref([])
const loadError = ref('')
const activeTab = ref('pages')
const templateLibraryTab = ref('all')
const customPageTemplates = ref([])
const templatesLoading = ref(false)
const previewTemplate = ref(null)

const showModal = ref(false)
const newTitle = ref('')
const newSlug = ref('')
const slugTouched = ref(false)
const loading = ref(false)
const error = ref('')
const copiedLink = ref('')
const selectedCreationTemplate = ref(null)
const saveTemplateOpen = ref(false)
const templateSavePage = ref(null)
const savedTemplateName = ref('')
const savedTemplateDescription = ref('')
const savingTemplate = ref(false)
const saveTemplateError = ref('')

// --- Delete confirmation modal state ---
const confirmOpen = ref(false)
const selectedPage = ref(null)
const isProcessing = ref(false)
const activeAction = ref('')

// --- Domain Handling ---
const domain = ref('')
const customDomain = ref('')
const verified = ref(false)
const liveDomain = ref('')

// Fetch creator domain
async function loadDomain() {
  if (process.env.NODE_ENV === 'development') {
    const devMemberOrigin =
      process.env.VUE_APP_MEMBER_PUBLIC_URL ||
      process.env.VUE_APP_MEMBER_APP_URL ||
      'http://localhost:3001'
    domain.value = devMemberOrigin.replace(/^https?:\/\//i, '')
    verified.value = false
    liveDomain.value = String(devMemberOrigin).replace(/\/+$/, '')
    console.log('[DEV] Using member app origin:', liveDomain.value)
    return
  }

  try {
    const res = await fetch(`${apiProxyUrl}/api/creator-domain`, {
      method: 'GET',
      credentials: 'include',
    })
    const data = await res.json()
    if (data.domain) {
      domain.value = data.domain
      customDomain.value = data.customDomain || ''
      verified.value = data.verified
      liveDomain.value =
        verified.value && customDomain.value
          ? `https://${customDomain.value}`
          : `https://${domain.value}`
    }
  } catch (err) {
    console.error('[CreatorPageEditor] Error fetching domain:', err)
  }
}

const searchQuery = ref('')

const builtInTemplates = pageTemplates.map(template => ({
  ...template,
  source: 'platforma',
  doc: buildPageTemplate(template.id),
}))

const summarizeTemplateDocument = (doc) => {
  const sections = Array.isArray(doc?.sections) ? doc.sections : []
  let elements = 0
  let images = 0
  const visit = (node) => {
    if (!node || typeof node !== 'object') return
    const type = String(node.type || '').toLowerCase()
    if (!['section', 'row'].includes(type)) elements += 1
    if (type === 'image') images += 1
    ;[...(Array.isArray(node?.props?.rows) ? node.props.rows : []), ...(Array.isArray(node?.props?.elements) ? node.props.elements : [])].forEach(visit)
  }
  sections.forEach(visit)
  return { sections: sections.length, elements, images }
}

const availableTemplates = computed(() => [
  ...builtInTemplates,
  ...customPageTemplates.value.map(item => ({
    id: `saved-${item.id}`,
    libraryId: item.id,
    name: item.name,
    description: item.description || 'A reusable page template saved from your Page Editor.',
    category: 'Your template',
    tags: ['saved', 'custom'],
    source: 'creator',
    doc: item.content,
    stats: summarizeTemplateDocument(item.content),
  })),
])

const filteredTemplates = computed(() => {
  const query = String(searchQuery.value || '').trim().toLowerCase()
  const source = templateLibraryTab.value === 'saved'
    ? availableTemplates.value.filter(template => template.source === 'creator')
    : availableTemplates.value
  if (!query) return source
  return source.filter(template => [template.name, template.description, template.category, ...(template.tags || [])]
    .some(value => String(value || '').toLowerCase().includes(query)))
})

const templateEmptyStateMessage = computed(() => {
  if (searchQuery.value) return `No templates match “${searchQuery.value}”.`
  if (templateLibraryTab.value === 'saved') return 'No saved templates yet. Save any page as a template to reuse it here.'
  return 'No templates are currently available.'
})

const filteredPages = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  const source = allPages.value.length ? allPages.value : pages.value
  const matches = source.filter(page => {
    if (isInternalManagedPage(page)) return false
    if (!q) return true
    const titleMatch = getDisplayPageTitle(page).toLowerCase().includes(q)
    const slugMatch  = page.slug?.toLowerCase().includes(q)
    const linkMatch  = liveDomain.value
      ? `${liveDomain.value}/${page.slug}`.toLowerCase().includes(q)
      : false
    return titleMatch || slugMatch || linkMatch
  })
  const start = (currentPage.value - 1) * itemsPerPage.value
  return matches.slice(start, start + itemsPerPage.value)
})

const filteredPageCount = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  const source = allPages.value.length ? allPages.value : pages.value
  return source.filter(page => {
    if (isInternalManagedPage(page)) return false
    if (!q) return true
    const titleMatch = getDisplayPageTitle(page).toLowerCase().includes(q)
    const slugMatch = page.slug?.toLowerCase().includes(q)
    const linkMatch = liveDomain.value
      ? `${liveDomain.value}/${page.slug}`.toLowerCase().includes(q)
      : false
    return titleMatch || slugMatch || linkMatch
  }).length
})

function normalizePageSlug(slugValue) {
  return String(slugValue || '')
    .trim()
    .toLowerCase()
    .replace(/^\/+/, '')
}

function isInternalFeatureLockPage(page) {
  const slug = normalizePageSlug(page?.slug)
  return slug.startsWith('feature-lock-')
}

function isInternalQuizPage(page) {
  const slug = normalizePageSlug(page?.slug)
  return /^quiz-\d+-(?:landing|result-\d+)$/.test(slug)
}

function slugifyPageTitle(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'new-page'
}

function makeUniquePageSlug(value) {
  const base = slugifyPageTitle(value)
  const used = new Set((allPages.value || []).map(page => normalizePageSlug(page?.slug)))
  if (!used.has(base)) return base
  let suffix = 2
  while (used.has(`${base}-${suffix}`)) suffix += 1
  return `${base}-${suffix}`
}

function openCreateModal(template = null) {
  selectedCreationTemplate.value = template || null
  const suggestedTitle = template?.id === 'blank' ? 'Untitled Page' : String(template?.name || '')
  newTitle.value = suggestedTitle
  newSlug.value = makeUniquePageSlug(suggestedTitle || 'new-page')
  slugTouched.value = false
  error.value = ''
  showModal.value = true
}

function onNewTitleInput() {
  if (!slugTouched.value) newSlug.value = makeUniquePageSlug(newTitle.value)
}

function onNewSlugInput() {
  slugTouched.value = true
  newSlug.value = slugifyPageTitle(newSlug.value)
}

async function loadCustomPageTemplates() {
  templatesLoading.value = true
  try {
    const response = await fetch(`${apiProxyUrl}/api/pages/library?type=page`, { credentials: 'include' })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data?.success) throw new Error(data?.message || 'Unable to load saved templates.')
    customPageTemplates.value = Array.isArray(data.items) ? data.items : []
  } catch (loadTemplateError) {
    console.error('[PageEditor] Error loading custom page templates:', loadTemplateError)
  } finally {
    templatesLoading.value = false
  }
}

function templateDocumentForCreation(template) {
  if (!template) return null
  const source = template.source === 'creator' ? template.doc : buildPageTemplate(template.id)
  if (!source || typeof source !== 'object') return null
  return JSON.parse(JSON.stringify(source))
}

async function applyCreationTemplate(pageId, template, title) {
  const doc = templateDocumentForCreation(template)
  if (!doc) return
  doc.title = title
  doc.seo = { ...(doc.seo || {}), title }
  const response = await fetch(`${apiProxyUrl}/api/pages/${pageId}/apply-template`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      title,
      templateSections: Array.isArray(doc.sections) ? doc.sections : [],
      templateKey: template.source === 'creator' ? `creator:${template.libraryId}` : template.id,
      templateVersion: '3',
    }),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok || !data?.success) throw new Error(data?.message || 'The page was created, but its template could not be applied.')
}

function openSavePageTemplate(page) {
  templateSavePage.value = page
  savedTemplateName.value = `${getDisplayPageTitle(page)} Template`
  savedTemplateDescription.value = ''
  saveTemplateError.value = ''
  saveTemplateOpen.value = true
}

async function savePageAsTemplate() {
  if (!templateSavePage.value?.id || !savedTemplateName.value.trim()) {
    saveTemplateError.value = 'Enter a name for this template.'
    return
  }
  savingTemplate.value = true
  saveTemplateError.value = ''
  try {
    const contentResponse = await fetch(`${apiProxyUrl}/api/pages/${templateSavePage.value.id}/content`, { credentials: 'include' })
    const contentData = await contentResponse.json().catch(() => ({}))
    if (!contentResponse.ok) throw new Error(contentData?.message || 'Unable to load this page.')
    let document = contentData?.draft || contentData?.published
    if (typeof document === 'string') {
      try { document = JSON.parse(document) } catch (_error) { document = null }
    }
    if (!document || typeof document !== 'object') throw new Error('This page does not have reusable content yet.')
    const response = await fetch(`${apiProxyUrl}/api/pages/library`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        itemType: 'page',
        name: savedTemplateName.value.trim(),
        description: savedTemplateDescription.value.trim(),
        content: document,
      }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data?.success) throw new Error(data?.message || 'Unable to save this template.')
    saveTemplateOpen.value = false
    await loadCustomPageTemplates()
    activeTab.value = 'templates'
    templateLibraryTab.value = 'saved'
    searchQuery.value = ''
  } catch (saveError) {
    saveTemplateError.value = saveError.message || 'Unable to save this template.'
  } finally {
    savingTemplate.value = false
  }
}

async function deleteCustomTemplate(template) {
  if (!template?.libraryId || !window.confirm(`Delete the saved template “${template.name}”?`)) return
  const response = await fetch(`${apiProxyUrl}/api/pages/library/${template.libraryId}`, { method: 'DELETE', credentials: 'include' })
  const data = await response.json().catch(() => ({}))
  if (!response.ok || !data?.success) {
    window.alert(data?.message || 'Unable to delete this saved template.')
    return
  }
  customPageTemplates.value = customPageTemplates.value.filter(item => Number(item.id) !== Number(template.libraryId))
  if (previewTemplate.value?.libraryId === template.libraryId) previewTemplate.value = null
}

function isInternalMeetPage(page) {
  const slug = normalizePageSlug(page?.slug)
  return /^meet-\d+-landing$/.test(slug)
}

function isInternalManagedPage(page) {
  return isInternalFeatureLockPage(page) || isInternalQuizPage(page) || isInternalMeetPage(page)
}

function getDisplayPageTitle(page) {
  const slug = normalizePageSlug(page?.slug)
  if (slug === '') return 'Home Page'
  if (slug === 'pricing') return 'Pricing Page'
  if (slug === 'join') return 'Join Page'
  if (slug === 'get-access') return 'Get Access Page'
  if (slug === 'get-access-success') return 'Get Access Success Page'
  if (slug === 'login') return 'Login Page'
  if (slug === 'member-approval-request') return 'Member Approval Request Page'
  if (slug === '404') return '404 Page'
  const raw = String(page?.seoTitle || page?.title || '').trim()
  return raw || 'Untitled Page'
}

// --- Pages Logic ---
const currentPage = ref(1)
const itemsPerPage = ref(5)
const rowsPerPageOptions = Object.freeze([
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
])
const totalItems = computed(() => filteredPageCount.value)
const totalPages = computed(() => Math.max(1, Math.ceil(totalItems.value / Math.max(1, Number(itemsPerPage.value || 1)))))

const startIndex = computed(() =>
  totalItems.value === 0 ? 0 : (currentPage.value - 1) * itemsPerPage.value + 1
)
const endIndex = computed(() =>
  Math.min(currentPage.value * itemsPerPage.value, totalItems.value)
)
async function hydrateSeoTitles(rows) {
  const api = apiProxyUrl
  await Promise.allSettled(
    rows.map(async (p) => {
      try {
        const r = await fetch(`${api}/api/pages/${p.id}/content`, { credentials: 'include' })
        if (!r.ok) return
        const data = await r.json()
        const doc = data?.draft || data?.published
        const seoTitle = doc?.seo?.title
        if (seoTitle) p.seoTitle = String(seoTitle)
      } catch {
        // ignore; leave row as-is
      }
    })
  )
}

// Updated loadPages with smart fallback pagination (non-breaking)
async function loadPages(page = 1) {
  loadError.value = ''
  try {
    const url = `${apiProxyUrl}/api/pages`
    const res = await fetch(url, { credentials: 'include' })
    const data = await res.json()

    if (data.success) {
      // Normalize slug (remove leading slash)
      const normalized = Array.isArray(data.pages)
        ? data.pages.map(p => ({ ...p, slug: p.slug?.replace(/^\/+/, '') || '' }))
        : []
      const visiblePages = normalized.filter(page => !isInternalManagedPage(page))

      allPages.value = visiblePages

      pages.value = visiblePages
      await hydrateSeoTitles(visiblePages)
      currentPage.value = Math.min(Math.max(1, page), totalPages.value)
    } else {
      console.error('Load failed', data)
      throw new Error('Load failed')
    }
  } catch (err) {
    console.error('[PageEditor] Error loading pages:', err)
    loadError.value = 'Unable to load real pages from the API. Please verify Creator BFF/Core backend are running and refresh.'
    pages.value = []
    allPages.value = []
    currentPage.value = 1
  }
}

// Page switcher
function changePage(page) {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
}

watch([searchQuery, itemsPerPage], () => {
  currentPage.value = 1
})

watch(totalPages, (nextTotal) => {
  if (currentPage.value > nextTotal) currentPage.value = nextTotal
})

// tiny helper to normalize ID from any response shape
function extractPageId(data) {
  return data?.page?.id ?? data?.id ?? data?.insertId ?? null
}

async function createPage() {
  if (!newTitle.value || !newSlug.value) {
    error.value = 'Both title and slug are required.'
    return
  }

  const cleanSlug = slugifyPageTitle(newSlug.value)
  const duplicate = (allPages.value || []).some(page => normalizePageSlug(page?.slug) === cleanSlug)
  if (duplicate) {
    error.value = `The page slug “${cleanSlug}” is already in use. Try “${makeUniquePageSlug(cleanSlug)}”.`
    return
  }

  loading.value = true
  error.value = ''

  try {
    console.log('[CreatePage][PROD] POST ->', `${apiProxyUrl}/api/pages`, {
      title: newTitle.value,
      slug: cleanSlug,
    })

    const res = await fetch(`${apiProxyUrl}/api/pages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        title: newTitle.value,
        slug: cleanSlug,
      }),
    })

    const text = await res.text()
    let data
    try {
      data = JSON.parse(text)
    } catch (e) {
      console.error('[CreatePage][PROD] Non-JSON response:', text)
      throw new Error('Create page returned non-JSON')
    }

    console.log('[CreatePage][PROD] HTTP', res.status, 'payload =', data)

    if (res.ok && data?.success) {
      const id = extractPageId(data)
      console.log('[CreatePage][PROD] Extracted pageId =', id)

      if (id) {
        if (selectedCreationTemplate.value) {
          try {
            await applyCreationTemplate(id, selectedCreationTemplate.value, newTitle.value.trim())
          } catch (templateError) {
            console.warn('[CreatePage] Backend template apply failed; handing the same document to the editor session.', templateError)
            const fallbackDoc = templateDocumentForCreation(selectedCreationTemplate.value)
            if (fallbackDoc) {
              fallbackDoc.title = newTitle.value.trim()
              fallbackDoc.seo = { ...(fallbackDoc.seo || {}), title: newTitle.value.trim() }
              try { sessionStorage.setItem(`pageTemplate:${id}`, JSON.stringify(fallbackDoc)) } catch (_error) { /* best effort */ }
            }
          }
        }
        // Set the destination before closing. TransitionRoot can finish immediately
        // when motion is reduced, so assigning this afterward can miss after-leave.
        pendingRedirect.value = {
          ...(selectedCreationTemplate.value
            ? { name: 'SitePageEditor', params: { pageId: String(id) } }
            : { name: 'PageThemeEditor', query: { pageId: String(id) } }),
        }
      }

      showModal.value = false
      newTitle.value = ''
      newSlug.value = ''
      selectedCreationTemplate.value = null
      slugTouched.value = false

      if (!id) {
        console.warn('[CreatePage][PROD] No id found in response, reloading list instead')
        await loadPages()
      }
    } else {
      console.error('[CreatePage][PROD] Backend error payload:', data)
      emitCreatorPlanLimitPrompt(data)
      error.value = data?.message || `Create failed (${res.status})`
    }
  } catch (err) {
    console.error('[CreatePage][PROD] Exception:', err)
    error.value = err?.message || 'Unexpected error occurred.'
  } finally {
    loading.value = false
  }
}

function onModalAfterLeave() {
  // runs after TransitionRoot finishes leaving
  if (pendingRedirect.value) {
    console.log('[CreatePage] Redirecting to:', pendingRedirect.value)
    router.push(pendingRedirect.value)
    pendingRedirect.value = null
  }
}


function openSiteEditor(page) {
  if (!page || !page.id) return
  // Open real page editor route in all environments (including local dev).
  router.push({
    name: 'SitePageEditor',
    params: { pageId: page.id },
  })
}


function openDeleteModal(page) {
  if (isProtectedOnboardingPage(page)) return
  selectedPage.value = page
  confirmOpen.value = true
}

async function confirmDelete() {
  if (!selectedPage.value || isProtectedOnboardingPage(selectedPage.value)) return
  try {
    isProcessing.value = true
    activeAction.value = 'delete'
    await fetch(`${apiProxyUrl}/api/pages/${selectedPage.value.id}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    await loadPages()
  } catch (err) {
    console.error('[PageEditor] Error deleting page:', err)
  } finally {
    isProcessing.value = false
    activeAction.value = ''
    confirmOpen.value = false
  }
}

// --- Copy Function ---
function copyLink(link) {
  navigator.clipboard.writeText(link).then(() => {
    copiedLink.value = link
    setTimeout(() => (copiedLink.value = ''), 2000)
  })
}

function isProtectedOnboardingPage(page) {
  const slug = String(page?.slug || '')
    .trim()
    .toLowerCase()
    .replace(/^\/+/, '')
  const title = String(page?.seoTitle || page?.title || '').trim().toLowerCase()

  if (
    slug === ''
    || slug === 'login'
    || slug === 'join'
    || slug === 'pricing'
    || slug === 'get-access'
    || slug === 'get-access-success'
    || slug === 'member-approval-request'
    || slug === '404'
  ) return true
  if (title.includes('login page') || title === 'login') return true
  if (title.includes('join page') || title === 'join') return true
  if (title.includes('get access')) return true
  if (title.includes('pricing')) return true
  if (title.includes('member approval request')) return true
  if (title === '404 page' || title.includes('page not found')) return true
  return false
}

onMounted(async () => {
  await Promise.all([loadPages(), loadDomain(), loadCustomPageTemplates()])
})
</script>



<style scoped>
.page-editor-tab-enter-active,
.page-editor-tab-leave-active,
.template-library-tab-enter-active,
.template-library-tab-leave-active {
  transition: opacity .16s ease, transform .18s cubic-bezier(.22, 1, .36, 1);
}

.page-editor-tab-enter-from,
.template-library-tab-enter-from {
  opacity: 0;
  transform: translateY(5px);
}

.page-editor-tab-leave-to,
.template-library-tab-leave-to {
  opacity: 0;
  transform: translateY(-2px);
}

.template-card-canvas {
  position: absolute;
  left: 50%;
  top: 0;
  width: 1200px;
  min-height: 760px;
  transform: translateX(-50%) scale(0.34);
  transform-origin: top center;
}

.page-template-preview-scroll,
.page-template-preview-canvas-scroll {
  scrollbar-color: transparent transparent;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
}

.page-template-preview-scroll::-webkit-scrollbar,
.page-template-preview-canvas-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.page-template-preview-scroll::-webkit-scrollbar-button,
.page-template-preview-canvas-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

.page-template-preview-scroll::-webkit-scrollbar-track,
.page-template-preview-canvas-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.page-template-preview-scroll::-webkit-scrollbar-thumb,
.page-template-preview-canvas-scroll::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background-color: transparent;
}

.page-template-preview-scroll:hover,
.page-template-preview-canvas-scroll:hover {
  scrollbar-color: rgba(145, 150, 156, 0.54) transparent;
}

.page-template-preview-scroll:hover::-webkit-scrollbar-thumb,
.page-template-preview-canvas-scroll:hover::-webkit-scrollbar-thumb {
  background-color: rgba(140, 143, 148, 0.48);
}

.page-template-preview-scroll:hover::-webkit-scrollbar-thumb:hover,
.page-template-preview-canvas-scroll:hover::-webkit-scrollbar-thumb:hover {
  background-color: rgba(100, 116, 139, 0.68);
}

@media (prefers-reduced-motion: reduce) {
  .page-editor-tab-enter-active,
  .page-editor-tab-leave-active,
  .template-library-tab-enter-active,
  .template-library-tab-leave-active {
    transition: none;
  }
}
</style>


  
  
